import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { AuthedAvatar } from "@/attendee/AuthedAvatar";
import ImageCropModal from "@/attendee/ImageCropModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, AlertTriangle, RefreshCw, Info } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS_IN = { 1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31 };
const NAME_MAX = 40;

export default function MyProfile() {
  const { eventId, attendeeId, refreshMyProfile } = useApp();
  const [state, setState] = useState("loading"); // loading | error | ok
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const fileRef = useRef(null);

  const hydrate = useCallback((d) => {
    setData(d);
    setName(d.display_name_is_custom ? d.display_name : "");
    setMonth(d.birthday_month ? String(d.birthday_month) : "");
    setDay(d.birthday_day ? String(d.birthday_day) : "");
    setOptIn(!!d.birthday_recognition_opt_in);
    setPhotoUrl(d.photo_url || null);
  }, []);

  const load = useCallback(() => {
    if (!attendeeId) return;
    setState("loading");
    api.getProfile(eventId, attendeeId)
      .then((d) => { hydrate(d); setState("ok"); })
      .catch(() => setState("error"));
  }, [eventId, attendeeId, hydrate]);

  useEffect(() => { load(); }, [load]);

  const effectiveName = (name.trim() || data?.fallback_display_name || "Attendee");

  const onPickPhoto = () => fileRef.current && fileRef.current.click();

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
      toast.error("Please choose a JPG, PNG or WEBP image. HEIC isn't supported yet.");
      return;
    }
    if (f.size > 6 * 1024 * 1024) { toast.error("Image is too large. Please choose one under 6 MB."); return; }
    // Open the crop/zoom/reposition preview before uploading.
    setCropFile(f);
  };

  const onCropConfirm = async (blob) => {
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "profile.jpg", { type: "image/jpeg" }));
      const res = await api.uploadProfilePhoto(eventId, attendeeId, fd);
      setPhotoUrl(res.photo_url);
      setCropFile(null);
      refreshMyProfile && refreshMyProfile();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that photo. Please try another.");
    } finally { setPhotoBusy(false); }
  };

  const onRemovePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.removeProfilePhoto(eventId, attendeeId);
      setPhotoUrl(null);
      refreshMyProfile && refreshMyProfile();
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error("Couldn't remove the photo. Please try again.");
    } finally { setPhotoBusy(false); }
  };

  const clearBirthday = () => { setMonth(""); setDay(""); setOptIn(false); };

  const save = async () => {
    if (name.trim().length > NAME_MAX) { toast.error(`Display name must be ${NAME_MAX} characters or fewer`); return; }
    if ((month && !day) || (!month && day)) { toast.error("Please choose both a birthday month and day, or clear both."); return; }
    setSaving(true);
    try {
      const body = {
        display_name: name.trim(),
        birthday_month: month ? Number(month) : null,
        birthday_day: day ? Number(day) : null,
        birthday_recognition_opt_in: !!(month && day && optIn),
      };
      const d = await api.updateProfile(eventId, attendeeId, body);
      hydrate(d);
      refreshMyProfile && refreshMyProfile();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your profile. Please try again.");
    } finally { setSaving(false); }
  };

  if (state === "loading") {
    return (
      <div className="pt-16 px-4 pb-10" data-testid="profile-loading">
        <div className="h-7 w-40 att-card animate-pulse mb-4" />
        <div className="h-64 att-card animate-pulse" />
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className="pt-16 px-4 pb-10">
        <div className="att-card p-6 text-center" data-testid="profile-error">
          <AlertTriangle className="h-6 w-6 text-[hsl(var(--pc-gold))] mx-auto mb-2" />
          <div className="font-serif text-lg text-cream">We couldn't load your profile</div>
          <button onClick={load} data-testid="profile-retry" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--pc-gold))] text-[hsl(var(--pc-ink))] px-4 py-1.5 text-xs font-semibold">
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const maxDay = month ? DAYS_IN[Number(month)] : 31;

  return (
    <div className="pt-16 px-4 pb-12" data-testid="my-profile">
      <div className="pt-2 pb-4">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Account</span>
        <h1 className="font-serif text-3xl text-cream leading-tight">My Profile</h1>
      </div>

      {/* Photo */}
      <div className="att-card p-5 flex items-center gap-4">
        <AuthedAvatar photoUrl={photoUrl} name={effectiveName} identityKey={attendeeId} className="h-20 w-20" textClass="text-xl" />
        <div className="flex-1">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={onPickPhoto} disabled={photoBusy} data-testid="profile-photo-upload">
              {photoBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}
              {photoUrl ? "Change" : "Upload"}
            </Button>
            {photoUrl && (
              <Button size="sm" variant="ghost" onClick={onRemovePhoto} disabled={photoBusy} data-testid="profile-photo-remove">
                <Trash2 className="h-4 w-4 mr-1.5" /> Remove
              </Button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-cream-dim leading-snug">Square photos look best. JPG, PNG or WEBP up to 6&nbsp;MB.</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="profile-photo-input" />
        </div>
      </div>

      {/* Display name */}
      <div className="att-card p-5 mt-3">
        <Label htmlFor="display-name" className="text-cream">Display name</Label>
        <Input id="display-name" value={name} maxLength={NAME_MAX}
          onChange={(e) => setName(e.target.value)}
          placeholder={data?.fallback_display_name || "Your name"}
          data-testid="profile-display-name" className="mt-1.5" />
        <p className="mt-1.5 text-[11px] text-cream-dim">{name.length}/{NAME_MAX} · Leave blank to use “{data?.fallback_display_name}”.</p>
        <div className="mt-2">
          <Label className="text-cream-dim text-xs">Registered email</Label>
          <div className="mt-1 text-sm text-cream-dim" data-testid="profile-email">{data?.email || "—"}</div>
        </div>
      </div>

      {/* Birthday */}
      <div className="att-card p-5 mt-3">
        <div className="flex items-center justify-between">
          <Label className="text-cream">Birthday <span className="text-cream-dim font-normal">(optional)</span></Label>
          {(month || day) && (
            <button onClick={clearBirthday} data-testid="profile-birthday-clear" className="text-[11px] text-[hsl(var(--pc-gold))] font-semibold">Clear</button>
          )}
        </div>
        <p className="text-[11px] text-cream-dim mt-0.5">Day and month only — no year.</p>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Select value={month} onValueChange={(v) => { setMonth(v); if (day && Number(day) > DAYS_IN[Number(v)]) setDay(""); }}>
            <SelectTrigger data-testid="profile-birthday-month"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={day} onValueChange={setDay} disabled={!month}>
            <SelectTrigger data-testid="profile-birthday-day"><SelectValue placeholder="Day" /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-4 flex items-start gap-3">
          <Switch checked={optIn} disabled={!(month && day)} onCheckedChange={setOptIn} data-testid="profile-birthday-optin" className="mt-0.5" />
          <label className="text-sm text-cream leading-snug" onClick={() => (month && day) && setOptIn(!optIn)}>
            I’m happy for the events team to recognise my birthday during the event.
          </label>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-3 flex items-start gap-2 px-1">
        <Info className="h-4 w-4 text-cream-dim shrink-0 mt-0.5" />
        <p className="text-[11px] text-cream-dim leading-snug">
          Your photo and display name appear to signed-in event attendees on the PC Quest leaderboard.
          Your official registration and travel details are managed separately.
        </p>
      </div>

      <Button onClick={save} disabled={saving} data-testid="profile-save" className="w-full mt-4">
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save profile
      </Button>

      <ImageCropModal
        file={cropFile}
        open={!!cropFile}
        busy={photoBusy}
        onCancel={() => setCropFile(null)}
        onConfirm={onCropConfirm}
      />
    </div>
  );
}
